import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Eye, EyeOff, Handshake } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SetuOSLogo } from '@/components/brand/SetuOSLogo';
import '@/styles/artis-brand.css';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await api.post('/api/login', {
        email,
        password,
      });

      const { token, user } = response.data.data;
      login(token, user);

      switch (user.role) {
        case 'SUPER_ADMIN':
          navigate('/super-admin/dashboard');
          break;
        case 'HOSPITAL_ADMIN':
          navigate('/hospital-admin/dashboard');
          break;
        case 'SALES_MANAGER':
          navigate('/sales-person/dashboard');
          break;
        case 'RECEPTIONIST':
        case 'OPERATIONS_MANAGER':
          navigate('/receptionist/dashboard');
          break;
        case 'SALES_PERSON':
          navigate('/sales-person/dashboard');
          break;
        case 'EXTERNAL_PARTNER':
          navigate('/sales-person/leads');
          break;
        case 'DOCTOR_TEAM_LEAD':
          navigate('/doctor-team/dashboard');
          break;
        case 'DOCTOR':
        case 'RMO':
          navigate('/doctor/dashboard');
          break;
        case 'LAB_TECHNICIAN':
          navigate('/lab/dashboard');
          break;
        case 'NURSE':
          navigate('/nurse/dashboard');
          break;
        case 'PHARMACIST':
          navigate('/pharmacist/dashboard');
          break;
        case 'CHARTERED_ACCOUNTANT':
          navigate('/chartered-accountant');
          break;
        case 'INVESTOR':
          navigate('/investor/business-reports');
          break;
        case 'VENDOR':
          navigate('/vendor/dashboard');
          break;
        case 'PHARMA_VENDOR':
          navigate('/pharma-vendor/orders');
          break;
        case 'BUSINESS_ANALYST':
          navigate('/hospital-admin/business-reports');
          break;
        default:
          navigate('/hospital-admin/dashboard');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');
    if (!forgotEmail.trim()) {
      setForgotError('Please enter your email address.');
      return;
    }
    setForgotLoading(true);
    try {
      await api.post('/api/forgot-password', { email: forgotEmail.trim() });
      setForgotMessage('If an account exists with this email, you will receive a password reset link shortly.');
    } catch (err: any) {
      setForgotError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="artis-theme min-h-screen flex font-artis-sans bg-artis-cream text-artis-brown">
      {/* Brand panel */}
      <div className="relative hidden lg:flex w-[46%] max-w-[720px] flex-col items-center justify-center overflow-hidden bg-artis-brown-dark px-12 py-16 xl:px-16">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 80% 55% at 50% -5%, rgba(176,122,62,0.26), transparent 60%), radial-gradient(ellipse 45% 35% at 100% 100%, rgba(111,85,61,0.2), transparent), radial-gradient(ellipse 40% 30% at 0% 80%, rgba(176,122,62,0.1), transparent)',
          }}
        />
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-artis-gold/40 to-transparent"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"
          aria-hidden
        />

        <div className="relative z-10 flex w-full max-w-xl flex-col items-center px-4 text-center">
          <SetuOSLogo framed className="max-w-[480px]" />
          <div className="mt-12 w-full max-w-md border-t border-white/10 pt-10">
            <h2 className="font-artis-serif text-3xl font-semibold tracking-tight text-white xl:text-4xl">
              Welcome back
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-artis-sand/80 xl:text-base">
              Sign in to manage hospital operations, staff, and patient records across your SetuOS network.
            </p>
          </div>
        </div>

        <p className="absolute bottom-8 text-[10px] font-medium uppercase tracking-[0.28em] text-artis-sand/50">
          SetuOS · Connecting care
        </p>
      </div>

      {/* Sign-in form */}
      <div className="relative flex flex-1 flex-col justify-center px-4 py-10 sm:px-8 lg:px-14 xl:px-20">
        <div
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background:
              'radial-gradient(ellipse 70% 50% at 100% 0%, rgba(176,122,62,0.08), transparent 55%), radial-gradient(ellipse 50% 40% at 0% 100%, rgba(227,225,210,0.55), transparent)',
          }}
        />

        <div className="relative mx-auto w-full max-w-[420px]">
          <div className="mb-8 text-center lg:text-left">
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-artis-gold">
              SetuOS Portal
            </p>
            <h1 className="font-artis-serif mt-2 text-3xl font-semibold text-artis-brown-dark xl:text-[2rem]">
              Sign in to your account
            </h1>
            <p className="mt-2 text-sm text-artis-slate">Secure access for hospital staff and administrators.</p>
          </div>

          <div className="rounded-3xl border border-artis-brown/8 bg-white/90 p-8 shadow-[0_20px_60px_-24px_rgba(42,27,17,0.18)] backdrop-blur-sm ring-1 ring-white/80">
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-artis-brown">
                    Email address
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 h-11 border-artis-brown/12 bg-white focus-visible:ring-artis-gold/35"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-artis-brown">
                    Password
                  </Label>
                  <div className="relative mt-1.5">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-11 border-artis-brown/12 bg-white pr-10 focus-visible:ring-artis-gold/35"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-artis-slate hover:text-artis-brown"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setForgotOpen(true);
                      setForgotEmail(email);
                      setForgotError('');
                      setForgotMessage('');
                    }}
                    className="text-sm font-medium text-artis-gold transition hover:text-artis-brown-dark"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>

              {error && (
                <div className="rounded-xl bg-red-50 px-3 py-2.5 text-center text-sm text-red-600 ring-1 ring-red-100">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="h-11 w-full rounded-full bg-artis-gold text-base font-medium text-white shadow-lg shadow-artis-gold/20 transition hover:bg-artis-gold/90"
              >
                {isLoading ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/partner/login"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-artis-brown/15 bg-white/80 px-5 py-2.5 text-sm font-medium text-artis-brown shadow-sm transition hover:border-artis-gold/35 hover:bg-white"
            >
              <Handshake className="h-4 w-4 text-artis-gold" />
              Partner portal login
            </Link>
            <p className="mt-3 text-xs text-artis-slate">
              Referring doctors and agents —{' '}
              <Link
                to="/partner/register"
                className="font-medium text-artis-gold hover:text-artis-brown-dark hover:underline"
              >
                register here
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="font-artis-sans sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-artis-serif text-artis-brown-dark">Reset password</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-artis-slate">
            Enter the email address you use to sign in. We&apos;ll send you a link to reset your password.
          </p>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <Label htmlFor="forgot-email" className="text-artis-brown">
                Email address
              </Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="you@example.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="mt-1 border-artis-brown/15 bg-artis-cream/40"
              />
            </div>
            {forgotError && <p className="text-sm text-red-600">{forgotError}</p>}
            {forgotMessage && <p className="text-sm text-green-700">{forgotMessage}</p>}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setForgotOpen(false)}
                className="border-artis-brown/20 text-artis-brown hover:bg-artis-cream"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={forgotLoading}
                className="bg-artis-gold text-white hover:bg-artis-gold/90"
              >
                {forgotLoading ? 'Sending…' : 'Send reset link'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
